/* eslint-disable no-unused-vars */
import 'semantic-ui-css/semantic.min.css';
import './_App.scss';
import React, { Suspense, lazy, useEffect } from 'react';
import {
  Navigate,
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ErrorBoundary } from 'components/common';
import { Layout } from 'layouts/Layout';
import { CartProvider } from 'context/cartContext';
import { appendAdobeAnalyticsScript } from 'libs/adobeAnalytics';
import { useNotificationScript } from 'controls/hooks/useNotificationScript';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { ChakraProvider, defaultSystem } from '@chakra-ui/react';
import { RouterErrorElement } from 'components/common/routerErrorElement';
import LoaderSpinner from 'components/atoms/Loader/LoaderSpinner';
import { useAuth } from 'context/AuthContext';
import { setUser } from 'app/api/userSlice';
import { rcpApiSlice } from 'app/api/rcpApiSlice';

// lazy load components
const Catalog = lazy(() => import('pages/Catalog/Catalog').then((m) => ({ default: m.Catalog })));
const Checkout = lazy(() => import('pages/Checkout/Checkout').then((m) => ({ default: m.Checkout })));
const ThankYou = lazy(() => import('pages/ThankYou/ThankYou'));
const OffersPage = lazy(() => import('pages/Offers/OfferPage').then((module) => ({ default: module.OffersPage })));

const TRACKING_ID = process.env.REACT_APP_GA4_TRACKING_ID;

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <ErrorBoundary>
          <Layout />
        </ErrorBoundary>),
      errorElement: <RouterErrorElement />,
      children: [
        { index: true, element: <Navigate to='/software' replace /> },
        { path: 'callback', element: <LoaderSpinner /> },
        {
          path: 'promos',
          element: <Suspense fallback={<LoaderSpinner />}><OffersPage /></Suspense>,
        },
        { path: 'checkout', element: <Suspense fallback={<LoaderSpinner />}><Checkout /></Suspense> },
        {
          path: 'thank-you',
          element: <Suspense fallback={<LoaderSpinner />}><ThankYou /></Suspense>,
        },
        { path: ':type', element: <Suspense fallback={<LoaderSpinner />}><Catalog /></Suspense> },
        { path: ':type/:category', element: <Suspense fallback={<LoaderSpinner />}><Catalog /></Suspense> },
        { path: ':type/:category/:product', element: <Suspense fallback={<LoaderSpinner />}><Catalog /></Suspense> },
        { path: '*', element: <p className='text-center text-2xl font-semibold mt-2'>Page Not Found</p> },
      ],
    },
  ],
  {
    basename: process.env.REACT_APP_BASE_PATH,
  },
);

export function App() {
  const notificationScript = useNotificationScript();
  const dispatch = useDispatch();
  const { user, isLoading } = useAuth();

  useEffect(() => {
    appendAdobeAnalyticsScript();
    ReactGA.initialize(TRACKING_ID);
  }, []);

  useEffect(() => {
    if (user) {
      dispatch(
        rcpApiSlice.endpoints.getProfile.initiate(user.id_token),
      ).then((result) => {
        const cultureCode = result?.data?.user?.cultureCode || 'en';
        dispatch(setUser({
          email: user.profile.email,
          name: user.profile.name,
          token: user.id_token,
          cultureCode,
        }));
      }).catch(() => {
        dispatch(setUser({
          email: user.profile.email,
          name: user.profile.name,
          token: user.id_token,
          cultureCode: 'en',
        }));
      });
    } else if (!isLoading) {
      dispatch(setUser({ guest: true }));
    }
  }, [user, isLoading, dispatch]);

  return (
    <CartProvider>
      <ChakraProvider value={defaultSystem}>
        <div className='App'>
          <Helmet>
            <title>Shop - Roland Cloud</title>
            <meta
              name='description'
              content='Shop Roland Cloud memberships, software instruments, and hardware-ready sounds for modern music creators.'
            />
            <link rel='canonical' href='https://www.roland.com/cloud/shop' />

            <meta property='og:title' content='Roland Cloud Shop | Roland' />
            <meta
              property='og:description'
              content='Shop Roland Cloud memberships, software instruments, and hardware-ready sounds for modern music creators.'
            />
            <meta property='og:url' content='https://www.roland.com/cloud/shop' />
            <meta property='og:type' content='website' />

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content='Roland Cloud Shop | Roland' />
            <meta
              name='twitter:description'
              content='Shop Roland Cloud memberships, software instruments, and hardware-ready sounds for modern music creators.'
            />
          </Helmet>

          {notificationScript && (
          <Helmet>
            <script>{notificationScript}</script>
          </Helmet>
          )}
          <link rel='preconnect' href='https://fonts.gstatic.com' />
          <link
            href='https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap'
            rel='stylesheet'
          />
          <link
            href='https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap'
            rel='stylesheet'
          />
          <RouterProvider router={router} />
        </div>
      </ChakraProvider>
    </CartProvider>
  );
}
